import { useMemo } from 'react';

import { useGetUserSettingsBillingStorageLimitLocking } from '@vimeo/gctl-api/hooks/useUserSettingsBillingStorageLimitLocking';
import { Video } from '@vimeo/gctl-api/models/components/schemas/video/Video';
import { useOrionSettings } from '@vimeo/orion';
import { deriveViewerAuthStatus } from '@vimeo/picox';
import { Viewer, useViewer } from '@vimeo/viewer';

const PURGATORY_PRIVACY = 'purgatory';

export const STORAGE_LIMIT_LOCKING_BYTES = 1024 * 1024 * 1024;
export const STORAGE_LIMIT_LOCKING_PRIVACY_FILTERS = {
  anybody: 'public',
  cold_storage: 'locked',
  disable: 'hide_from_vimeo',
  nobody: 'private',
  password: 'password',
  purgatory: 'locked',
  team: 'team',
  unlisted: 'unlisted',
} as const;

export type StorageLimitLockingVideo = Pick<
  Video,
  'createdTime' | 'filesSize' | 'isColdStorage' | 'name' | 'pictures' | 'privacy' | 'uri'
>;
export type StorageLimitLockingPrivacyFilterValue =
  (typeof STORAGE_LIMIT_LOCKING_PRIVACY_FILTERS)[keyof typeof STORAGE_LIMIT_LOCKING_PRIVACY_FILTERS];

type StorageLimitLockingResult = {
  lockedVideoUris: Set<string>;
  lockedVideosPreview: StorageLimitLockingVideo[];
  lockedVideoIdsTruncated: boolean;
};

export const isStorageLimitLockingEligible = (viewer: Viewer | null, isFlagEnabled: boolean) => {
  return isFlagEnabled && deriveViewerAuthStatus(viewer) === 'free';
};

export const useStorageLimitLockingEligibility = () => {
  const viewer = useViewer();
  const { settings } = useOrionSettings();

  return isStorageLimitLockingEligible(viewer, settings.enable_cold_storage_library_ux ?? false);
};

export const isStorageLimitLockingOverLimit = (
  totalUsedBytes: number | null | undefined,
  limitBytes: number = STORAGE_LIMIT_LOCKING_BYTES,
) => {
  if (!Number.isFinite(totalUsedBytes)) {
    return false;
  }

  return Number(totalUsedBytes) > limitBytes;
};

export const shouldIncludeBackendColdStorageClips = (
  isStorageLimitLockingEnabled: boolean,
  hasColdStorageClips: boolean,
) => {
  // The experiment computes locked state on the client, so backend cold-storage clips
  // must still be included in the response and then overridden client-side.
  return isStorageLimitLockingEnabled || hasColdStorageClips;
};

export const applyStorageLimitLockingToVideo = <T extends StorageLimitLockingVideo>(video: T) => {
  return {
    ...video,
    isColdStorage: true,
    privacy: {
      ...video.privacy,
      view: PURGATORY_PRIVACY,
    },
  };
};

export const applyStorageLimitLockingStateToVideo = <T extends StorageLimitLockingVideo>(
  video: T,
  lockedVideoUris: Set<string>,
) => {
  if (video.uri && lockedVideoUris.has(video.uri)) {
    return applyStorageLimitLockingToVideo(video);
  }

  return video;
};

export const getStorageLimitLockingPrivacyFilterValue = (
  privacyView: string | null | undefined,
): StorageLimitLockingPrivacyFilterValue | null => {
  if (!privacyView || !(privacyView in STORAGE_LIMIT_LOCKING_PRIVACY_FILTERS)) {
    return null;
  }

  return STORAGE_LIMIT_LOCKING_PRIVACY_FILTERS[
    privacyView as keyof typeof STORAGE_LIMIT_LOCKING_PRIVACY_FILTERS
  ];
};

export const useStorageLimitLocking = ({ userId }: { userId?: number | null }) => {
  const isEligible = useStorageLimitLockingEligibility();

  const { data, isLoading } = useGetUserSettingsBillingStorageLimitLocking(
    () => {
      if (!userId || !isEligible) {
        return null;
      }

      return {
        where: { userId },
        select: ['lockedVideoCount', 'lockedVideoIds', 'lockedVideosPreview'] as const,
      };
    },
    {
      revalidateOnFocus: false,
    },
  );

  const result = useMemo<StorageLimitLockingResult>(() => {
    const lockedVideoUris = new Set((data?.lockedVideoIds ?? []).map((id) => `/videos/${id}`));
    const lockedVideosPreview = (data?.lockedVideosPreview ?? []).map((video) =>
      applyStorageLimitLockingToVideo({
        createdTime: '',
        filesSize: {
          fileSizeType: 'UPLOAD',
          numVersions: 0,
          totalSize: 0,
        },
        isColdStorage: false,
        name: video.title,
        pictures: {
          sizes: video.thumbnail ? [{ link: video.thumbnail }] : [],
        } as StorageLimitLockingVideo['pictures'],
        privacy: {
          add: false,
          allowShareLink: false,
          comments: 'nobody',
          download: false,
          embed: 'private',
          view: 'nobody',
        },
        uri: video.uri,
      }),
    );

    return {
      lockedVideoUris,
      lockedVideosPreview,
      lockedVideoIdsTruncated: (data?.lockedVideoCount ?? 0) > (data?.lockedVideoIds?.length ?? 0),
    };
  }, [data]);

  // Frontend still owns experiment gating. Once enabled, the backend owns the
  // temporary lock computation and returns only the capped IDs and preview data.
  const hasResolvedAllPages = !isEligible || Boolean(data);

  return {
    hasResolvedAllPages,
    isEnabled: isEligible,
    isLoading: isEligible && !data && isLoading,
    isOverLimit: (data?.lockedVideoCount ?? 0) > 0,
    lockedVideoCount: data?.lockedVideoCount ?? 0,
    lockedVideoUris: result.lockedVideoUris,
    lockedVideosPreview: result.lockedVideosPreview,
    lockedVideoIdsTruncated: result.lockedVideoIdsTruncated,
  };
};
